import type { ApolloClient, ApolloError } from '@apollo/client';
import { SAVE_DOCUMENT_TEXT_MUTATION } from '../graphql/mutations';
import { DOCUMENT_CONTENT_QUERY } from '../graphql/queries';

/**
 * The offline write outbox (ROADMAP "PWA enhancements: offline support").
 *
 * Every user document write is one uniform operation — `saveDocumentText`
 * with the FULL blob — so offline support is a per-path queue of "path →
 * text" waiting for the network, not a replay log of structured operations.
 * A save that fails at the fetch layer (offline, not a server response) is
 * queued here and the new text is written into the Apollo cache so the UI
 * keeps showing it; `flushOutbox` replays the queue when connectivity is
 * back.
 *
 * Conflicts are ASK-FIRST: a flush keeps the compare-and-swap
 * (`expectedVersion` is the version the offline edit was based on), and a
 * CONFLICT marks the entry instead of overwriting — the document page then
 * offers "review your version" / "discard", never a silent clobber.
 *
 * The queue lives in localStorage so it survives reloads and app restarts;
 * `subscribeOutbox` covers both same-tab changes (a custom event) and other
 * tabs (the native `storage` event).
 */

export interface OutboxEntry {
  /** The document's unified path. */
  path: string;
  /** The full blob to save — the newest offline edit wins per path. */
  content: string;
  /**
   * `documentContent.version` the offline edit was based on (the CAS guard),
   * or null when the document didn't exist yet (a create).
   */
  expectedVersion: number | null;
  /** When the FIRST offline edit for this path was queued (ISO). */
  queuedAt: string;
  /** 'pending' flushes on the next sync; 'conflict' waits for the user. */
  status: 'pending' | 'conflict';
}

const OUTBOX_KEY = 'alma-outbox';
const OUTBOX_EVENT = 'alma-outbox-change';

interface DocumentContentData {
  documentContent: {
    __typename?: string;
    path: string;
    content: string;
    updatedAt: string | null;
    version: number;
  } | null;
}

// localStorage can be unavailable (private mode quotas, disabled storage);
// the outbox then degrades to "offline saves fail like before" — never a throw
// into the save path.
function readEntries(): OutboxEntry[] {
  try {
    const raw = window.localStorage.getItem(OUTBOX_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is OutboxEntry =>
        !!e &&
        typeof (e as OutboxEntry).path === 'string' &&
        typeof (e as OutboxEntry).content === 'string' &&
        ((e as OutboxEntry).status === 'pending' || (e as OutboxEntry).status === 'conflict')
    );
  } catch {
    return [];
  }
}

function writeEntries(entries: OutboxEntry[]): void {
  try {
    if (entries.length === 0) {
      window.localStorage.removeItem(OUTBOX_KEY);
    } else {
      window.localStorage.setItem(OUTBOX_KEY, JSON.stringify(entries));
    }
  } catch {
    // Quota or storage unavailable — nothing to do; the entry list in memory
    // for this call is lost, and the next save attempt reports normally.
  }
  try {
    window.dispatchEvent(new CustomEvent(OUTBOX_EVENT));
  } catch {
    // CustomEvent unavailable — subscribers just won't live-update.
  }
}

/** Every queued entry, pending and conflict alike. */
export function listOutbox(): OutboxEntry[] {
  return readEntries();
}

/** The queued entry for one path, if any. */
export function getOutboxEntry(path: string): OutboxEntry | null {
  return readEntries().find((e) => e.path === path) ?? null;
}

/** True when at least one entry is waiting to flush. */
export function hasPendingOutboxEntries(): boolean {
  return readEntries().some((e) => e.status === 'pending');
}

/**
 * Queue an offline save. One entry per path — a second offline edit replaces
 * the queued content but keeps the ORIGINAL `expectedVersion` (the server
 * version the offline editing session started from), so the eventual flush
 * still compare-and-swaps against the version the user last saw from the
 * server. Re-queueing over a 'conflict' entry (the user reviewed and edited
 * again while still offline) resets it to 'pending' on the freshly-reviewed
 * base version.
 */
export function enqueueSave(path: string, content: string, expectedVersion: number | null): void {
  const entries = readEntries();
  const existing = entries.find((e) => e.path === path);
  if (existing) {
    existing.content = content;
    if (existing.status === 'conflict') {
      existing.expectedVersion = expectedVersion;
      existing.status = 'pending';
    }
  } else {
    entries.push({
      path,
      content,
      expectedVersion,
      queuedAt: new Date().toISOString(),
      status: 'pending',
    });
  }
  writeEntries(entries);
}

/** Drop the queued entry for a path (synced, superseded, or discarded). */
export function removeOutboxEntry(path: string): void {
  const entries = readEntries();
  const next = entries.filter((e) => e.path !== path);
  if (next.length !== entries.length) writeEntries(next);
}

/** Mark a path's entry as needing the user's decision (flush hit CONFLICT). */
export function markOutboxConflict(path: string): void {
  const entries = readEntries();
  const entry = entries.find((e) => e.path === path);
  if (entry && entry.status !== 'conflict') {
    entry.status = 'conflict';
    writeEntries(entries);
  }
}

/** Clear the whole queue (logout). */
export function clearOutbox(): void {
  writeEntries([]);
}

/**
 * Subscribe to outbox changes — same-tab (custom event) and cross-tab (the
 * native `storage` event). Returns the unsubscribe function.
 */
export function subscribeOutbox(onChange: () => void): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.key === null || e.key === OUTBOX_KEY) onChange();
  };
  window.addEventListener(OUTBOX_EVENT, onChange);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(OUTBOX_EVENT, onChange);
    window.removeEventListener('storage', onStorage);
  };
}

/**
 * True for a save failure at the FETCH layer — offline, DNS, connection
 * reset — where queueing is honest. An HTTP response (4xx/5xx carries a
 * statusCode) or a GraphQL error is the server talking, and those keep their
 * existing error paths.
 */
export function isOfflineError(err: unknown): boolean {
  const apolloErr = err as Partial<ApolloError> | null;
  if (!apolloErr || typeof apolloErr !== 'object') return false;
  if (apolloErr.graphQLErrors && apolloErr.graphQLErrors.length > 0) return false;
  const net = apolloErr.networkError as (Error & { statusCode?: number }) | null | undefined;
  return net != null && net.statusCode === undefined;
}

function isConflictError(err: unknown): boolean {
  const apolloErr = err as Partial<ApolloError> | null;
  return apolloErr?.graphQLErrors?.[0]?.extensions?.code === 'CONFLICT';
}

/**
 * Write a queued save's content into the Apollo cache so every surface (the
 * document page, list/journal views, previews) keeps showing the offline
 * edit. The version is NOT bumped — it still names the last server version,
 * which is exactly what later edits should base their CAS on.
 */
export function applyLocalSave(client: ApolloClient<object>, path: string, content: string): void {
  let previous: DocumentContentData['documentContent'] = null;
  try {
    previous =
      client.readQuery<DocumentContentData>({
        query: DOCUMENT_CONTENT_QUERY,
        variables: { path },
      })?.documentContent ?? null;
  } catch {
    previous = null;
  }
  client.writeQuery<DocumentContentData>({
    query: DOCUMENT_CONTENT_QUERY,
    variables: { path },
    data: {
      documentContent: {
        __typename: 'DocumentContent',
        path,
        content,
        updatedAt: new Date().toISOString(),
        version: previous?.version ?? 0,
      },
    },
  });
}

type FlushProbe = 'applied' | 'stale' | 'unknown';

// On CONFLICT, check whether the server already holds exactly the queued
// content — another tab or device may have flushed the same edit — before
// bothering the user. 'unknown' (the probe itself failed, e.g. we dropped
// back offline) leaves the entry pending for the next flush.
async function probeQueuedContent(
  client: ApolloClient<object>,
  entry: OutboxEntry
): Promise<FlushProbe> {
  try {
    const { data } = await client.query<DocumentContentData>({
      query: DOCUMENT_CONTENT_QUERY,
      variables: { path: entry.path },
      fetchPolicy: 'network-only',
    });
    return data?.documentContent?.content === entry.content ? 'applied' : 'stale';
  } catch {
    return 'unknown';
  }
}

let flushing = false;

/**
 * Replay every pending entry against the server, oldest first. Success
 * updates the cached `documentContent` (content AND the new version) and
 * drops the entry; CONFLICT marks it for the user (ask-first — never an
 * overwrite); a fetch-layer failure stops the flush to retry later. Safe to
 * call any time — it no-ops when the queue is empty or a flush is running.
 */
export async function flushOutbox(client: ApolloClient<object>): Promise<void> {
  if (flushing) return;
  const pending = readEntries().filter((e) => e.status === 'pending');
  if (pending.length === 0) return;
  flushing = true;
  let flushedAny = false;
  try {
    for (const entry of pending) {
      try {
        const result = await client.mutate<{
          saveDocumentText: NonNullable<DocumentContentData['documentContent']>;
        }>({
          mutation: SAVE_DOCUMENT_TEXT_MUTATION,
          variables: {
            path: entry.path,
            content: entry.content,
            expectedVersion: entry.expectedVersion,
          },
        });
        const saved = result.data?.saveDocumentText;
        if (saved) {
          client.writeQuery<DocumentContentData>({
            query: DOCUMENT_CONTENT_QUERY,
            variables: { path: entry.path },
            data: { documentContent: saved },
          });
        }
        removeOutboxEntry(entry.path);
        flushedAny = true;
      } catch (err) {
        if (isConflictError(err)) {
          const probe = await probeQueuedContent(client, entry);
          if (probe === 'applied') {
            removeOutboxEntry(entry.path);
            flushedAny = true;
          } else if (probe === 'stale') {
            markOutboxConflict(entry.path);
          } else {
            break; // connectivity flaked mid-probe — retry the flush later
          }
        } else {
          break; // still offline (or the server is unhappy) — retry later
        }
      }
    }
    if (flushedAny) {
      // Refresh the tree list so updatedAt ordering and previews catch up.
      await client.refetchQueries({ include: ['Documents'] }).catch(() => {});
    }
  } finally {
    flushing = false;
  }
}
